import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { AlertTriangle, X, Calculator, Mail } from 'lucide-react';
export function FinalPostscriptsSection() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.05
  });
  return <section className="relative py-12 sm:py-16 md:py-20 px-5 sm:px-6 bg-gradient-to-b from-black via-purple-950/20 to-black overflow-hidden" data-id="element-432" data-mp="1mqe07xs14rj8">
      {/* Background effects - matching reference snippet */}
      <div className="absolute inset-0 opacity-10" style={{
      backgroundImage: `radial-gradient(circle at 20% 50%, rgba(168, 85, 247, 0.3) 0%, transparent 50%),
                           radial-gradient(circle at 80% 50%, rgba(59, 130, 246, 0.3) 0%, transparent 50%)`
    }} data-id="element-433" data-mp="u0f5pmv2g276" />

      {/* Glowing orbs */}
      <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-red-600/10 rounded-full blur-3xl animate-pulse-slow" data-id="element-434" data-mp="2aef3ar6fvfr9" />
      <div className="absolute bottom-1/4 right-1/3 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl animate-pulse-slow" style={{
      animationDelay: '1s'
    }} data-id="element-435" data-mp="2efh3pbc3dxut" />

      <div ref={ref} className="relative z-10 max-w-4xl mx-auto" data-id="element-436" data-mp="2ow2xggz3f4uj">
        {/* P.S. - THE MISINFORMATION ERA IS ACCELERATING */}
        <div className="relative mb-16" data-id="element-437" data-mp="2jvongtdxlktv">
          <div className="absolute inset-0 bg-gradient-radial from-amber-600/20 via-transparent to-transparent blur-3xl" data-id="element-438" data-mp="2fvo9taynpdnv" />

          <div className="relative bg-gradient-to-br from-amber-950/50 via-stone-950/80 to-black border-2 border-amber-600/60 p-5 sm:p-8 md:p-10 rounded-lg shadow-[0_0_16px_rgba(217,119,6,0.24)] sm:shadow-[0_0_50px_rgba(217,119,6,0.4)]" data-id="element-439" data-mp="2awuvxhfgjkn6">
            {/* Icon */}
            <div className="flex justify-center mb-6" data-id="element-440" data-mp="214fcsgsfcq0p">
              <div className="relative" data-id="element-441" data-mp="1w901nwlsx87z">
                <div className="absolute inset-0 bg-amber-500/40 blur-xl animate-pulse-slow" data-id="element-442" data-mp="1w5udqkowscdu" />
                <AlertTriangle className="relative w-12 h-12 text-amber-400 drop-shadow-[0_0_15px_rgba(217,119,6,0.8)]" data-id="element-443" data-mp="1s7wndejejk8v" />
              </div>
            </div>

            {/* Title */}
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-amber-400 mb-6 text-center" style={{
            textShadow: '0 0 30px rgba(217, 119, 6, 0.5)'
          }} data-id="element-444" data-mp="1di3dtyp07h3w">
              P.S. – The Misinformation Era Is Accelerating
            </h3>

            {/* Content */}
            <div className="space-y-4 text-lg text-stone-300 mb-8" data-id="element-445" data-mp="18ktosk5j17gl">
              <p className="text-stone-100" data-id="element-446" data-mp="1w9mie9yof381">
                Every day that passes, the gap between signal and noise gets
                wider.
              </p>
              <p className="text-stone-100" data-id="element-447" data-mp="1mk2wevfo2t8y">
                Every day you wait, more dangerous misinformation compounds.
              </p>
              <p className="text-stone-100" data-id="element-448" data-mp="1ri4ebd1cwmiu">
                Every day you remain in the competence gap, your biology decays
                irreversibly.
              </p>

              <div className="bg-amber-950/30 border-l-4 border-amber-500 p-6 my-8" data-id="element-449" data-mp="2jz72iw6kg6iw">
                <p className="text-xl font-bold text-amber-400 mb-4" data-id="element-450" data-mp="8khuphr0s6w8">
                  You have two choices:
                </p>
                <div className="space-y-4" data-id="element-451" data-mp="3m6m1acgo2fl">
                  <p className="text-stone-100" data-id="element-452" data-mp="3jap9pccnjsq">
                    <span className="font-bold text-red-400" data-id="element-453" data-mp="hd1mpzct3d83">1)</span> Continue
                    piecing together fragments from influencers who don't
                    actually understand the source material
                  </p>
                  <p className="text-stone-100" data-id="element-454" data-mp="3lrjk8rirr1px">
                    <span className="font-bold text-green-400" data-id="element-455" data-mp="3gs6rxr94p2fy">2)</span> Get
                    direct access to the two people who literally wrote THE book
                    on peptides... PLUS get Peptide Mastery 2.0 included with
                    your membership... PLUS make the paradigm shift that changes
                    everything
                  </p>
                </div>
              </div>

              <div className="text-center space-y-4 mt-8" data-id="element-456" data-mp="152w2aooiffw1">
                <p className="text-xl font-bold text-stone-100" data-id="element-457" data-mp="154ql9xg80xxh">
                  The back door is open.
                </p>
                <p className="text-xl text-stone-100" data-id="element-458" data-mp="25bdriwp538cz">And it's open right now.</p>
              </div>
            </div>

            {/* CTA */}
            <a href="https://go.peptidescienceinstitute.com/pm-pro-monthly-fm" className="block w-full px-5 sm:px-8 py-5 sm:py-6 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-white text-center font-bold text-base sm:text-xl rounded-lg transition-all duration-300 shadow-[0_0_24px_rgba(217,119,6,0.4)] sm:shadow-[0_0_40px_rgba(217,119,6,0.5)] sm:hover:shadow-[0_0_60px_rgba(217,119,6,0.7)] sm:hover:scale-105 min-h-[44px]" data-id="element-459" data-mp="2a95klu1it0ir">
              <span className="hidden sm:inline" data-id="element-460" data-mp="2y0kqtxwf41za">→ </span>STEP THROUGH THE BACK DOOR<span className="hidden sm:inline" data-id="element-461" data-mp="32ze5vurcs11k"> ←</span>
            </a>
          </div>
        </div>

        {/* P.P.S. - WHAT IF YOU DO NOTHING? */}
        <div className="relative mb-16" data-id="element-462" data-mp="371snycn76wrs">
          <div className="absolute inset-0 bg-gradient-radial from-red-600/20 via-transparent to-transparent blur-3xl" data-id="element-463" data-mp="36ymxou2u1ouh" />

          <div className="relative bg-gradient-to-br from-red-950/50 via-stone-950/80 to-black border-2 border-red-600/60 p-5 sm:p-8 md:p-10 rounded-lg shadow-[0_0_16px_rgba(239,68,68,0.24)] sm:shadow-[0_0_50px_rgba(239,68,68,0.4)]" data-id="element-464" data-mp="rcaqcru6tmdz">
            {/* Title */}
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-red-400 mb-6 text-center" style={{
            textShadow: '0 0 30px rgba(239, 68, 68, 0.5)'
          }} data-id="element-465" data-mp="hfz8znfqq6co">
              P.P.S. – What If You Do Nothing?
            </h3>

            {/* Content */}
            <div className="space-y-4 text-lg mb-8" data-id="element-466" data-mp="15aip95asaxrs">
              <p className="text-xl text-stone-100 text-center mb-6" data-id="element-467" data-mp="10d0fjcoy0eiq">
                Six months from now, if you chose not to take action today:
              </p>

              <div className="space-y-3" data-id="element-468" data-mp="1xtfo99xjwti7">
                <div className="flex items-start gap-3 bg-red-950/30 border-l-4 border-red-500 p-4 rounded-r-lg" data-id="element-469" data-mp="1t0waabuybwcv">
                  <X className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" data-id="element-470" data-mp="3n903v24urvh8" />
                  <p className="text-stone-100" data-id="element-471" data-mp="3ralpbbvgdi31">
                    You'll still be in the competence gap
                  </p>
                </div>

                <div className="flex items-start gap-3 bg-red-950/30 border-l-4 border-red-500 p-4 rounded-r-lg" data-id="element-472" data-mp="3dixc159gwnkh">
                  <X className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" data-id="element-473" data-mp="3igfc065ibsgc" />
                  <p className="text-stone-100" data-id="element-474" data-mp="33khmv7u03odm">
                    You'll still be guessing about your protocols
                  </p>
                </div>

                <div className="flex items-start gap-3 bg-red-950/30 border-l-4 border-red-500 p-4 rounded-r-lg" data-id="element-475" data-mp="2ukuicd5z62gc">
                  <X className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" data-id="element-476" data-mp="2ziw1ey3ei1rt" />
                  <p className="text-stone-100" data-id="element-477" data-mp="st7wsr4uqzr9">
                    You'll still be wondering if you're making mistakes that
                    won't show up for years
                  </p>
                </div>

                <div className="flex items-start gap-3 bg-red-950/30 border-l-4 border-red-500 p-4 rounded-r-lg" data-id="element-478" data-mp="17vpy2c38cs9p">
                  <X className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" data-id="element-479" data-mp="12y7ku9i6jqvw" />
                  <p className="text-stone-100" data-id="element-480" data-mp="1gob36pdxv0g2">
                    You'll still be operating at the same biological level
                  </p>
                </div>

                <div className="flex items-start gap-3 bg-red-950/30 border-l-4 border-red-500 p-4 rounded-r-lg" data-id="element-481" data-mp="1bvqbpuii8zfp">
                  <X className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" data-id="element-482" data-mp="1lnmli2tyedzr" />
                  <p className="text-stone-100" data-id="element-483" data-mp="1lne9ed5nncpm">
                    You'll still be six months older with six months of
                    irreversible decay
                  </p>
                </div>

                <div className="flex items-start gap-3 bg-red-950/30 border-l-4 border-red-500 p-4 rounded-r-lg" data-id="element-484" data-mp="1vjpkx0xf7yj0">
                  <X className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" data-id="element-485" data-mp="5b7fqgdig59u" />
                  <p className="text-stone-100" data-id="element-486" data-mp="19lwmdaduur7">
                    You'll have NEVER made the paradigm shift
                  </p>
                </div>

                <div className="flex items-start gap-3 bg-red-950/30 border-l-4 border-red-500 p-4 rounded-r-lg" data-id="element-487" data-mp="ur0t9io6lv7w">
                  <X className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" data-id="element-488" data-mp="zjll4enj1y8s" />
                  <p className="text-stone-100" data-id="element-489" data-mp="13knmp24x2mdx">
                    You'll have NEVER discovered what's truly possible
                  </p>
                </div>

                <div className="flex items-start gap-3 bg-red-950/30 border-l-4 border-red-500 p-4 rounded-r-lg" data-id="element-490" data-mp="6vy6rhcuo3ul">
                  <X className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" data-id="element-491" data-mp="fujiasjwmilv" />
                  <p className="text-stone-100" data-id="element-492" data-mp="ft8i12po3szi">
                    You'll STILL be relying on fragmented information from
                    people who don't know the full picture
                  </p>
                </div>
              </div>

              <div className="text-center space-y-4 mt-8" data-id="element-493" data-mp="1w89ygxoasxbv">
                <p className="text-xl font-bold text-stone-100" data-id="element-494" data-mp="1waw6eyf3peum">
                  And you'll wish you had taken action when you had the chance.
                </p>
                <p className="text-2xl font-bold text-red-400" data-id="element-495" data-mp="21eyco5bbzdya">
                  Don't let that be your story.
                </p>
              </div>
            </div>

            {/* CTA */}
            <a href="https://go.peptidescienceinstitute.com/pm-pro-monthly-fm" className="block w-full px-5 sm:px-8 py-5 sm:py-6 bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white text-center font-bold text-base sm:text-xl rounded-lg transition-all duration-300 shadow-[0_0_24px_rgba(239,68,68,0.4)] sm:shadow-[0_0_40px_rgba(239,68,68,0.5)] sm:hover:shadow-[0_0_60px_rgba(239,68,68,0.7)] sm:hover:scale-105 min-h-[44px]" data-id="element-496" data-mp="1skaaio11ycdp">
              <span className="hidden sm:inline" data-id="element-497" data-mp="3823pk9p3vzz1">→ </span>JOIN PSI PRO NOW<span className="hidden sm:inline" data-id="element-498" data-mp="381k6gpnpz5jf"> ←</span>
            </a>
          </div>
        </div>

        {/* P.P.P.S. - A THOUGHT EXPERIMENT */}
        <div className="relative mb-16" data-id="element-499" data-mp="1r9xvsxvpt4a9">
          <div className="absolute inset-0 bg-gradient-radial from-blue-600/20 via-transparent to-transparent blur-3xl" data-id="element-500" data-mp="1r8mo4mz5fcdv" />

          <div className="relative bg-gradient-to-br from-blue-950/50 via-stone-950/80 to-black border-2 border-blue-600/60 p-5 sm:p-8 md:p-10 rounded-lg shadow-[0_0_16px_rgba(59,130,246,0.24)] sm:shadow-[0_0_50px_rgba(59,130,246,0.4)]" data-id="element-501" data-mp="1naoyjj1guo9y">
            {/* Icon */}
            <div className="flex justify-center mb-6" data-id="element-502" data-mp="18khqzyczypo1">
              <div className="relative" data-id="element-503" data-mp="13kmwlku0jhy8">
                <div className="absolute inset-0 bg-blue-500/40 blur-xl animate-pulse-slow" data-id="element-504" data-mp="13ly2praxl1si" />
                <Calculator className="relative w-12 h-12 text-blue-400 drop-shadow-[0_0_15px_rgba(59,130,246,0.8)]" data-id="element-505" data-mp="1iakr0iijd3vv" />
              </div>
            </div>

            {/* Title */}
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-blue-400 mb-6 text-center" style={{
            textShadow: '0 0 30px rgba(59, 130, 246, 0.5)'
          }} data-id="element-506" data-mp="psozy9hhneiy">
              P.P.P.S. – A Thought Experiment
            </h3>

            {/* Content */}
            <div className="space-y-4 text-lg text-stone-300 mb-8" data-id="element-507" data-mp="3187beg0p5njj">
              <p className="text-xl text-stone-100 text-center mb-6" data-id="element-508" data-mp="319ieseq8cta4">
                What would it be worth to you to avoid even ONE major mistake
                with peptides?
              </p>

              <div className="bg-blue-950/30 border-l-4 border-blue-500 p-6 my-6" data-id="element-509" data-mp="2rinpt2llnfkz">
                <p className="text-xl font-bold text-blue-400 mb-4" data-id="element-510" data-mp="3f8i2nk5x5458">
                  A bad protocol could cost you:
                </p>
                <div className="space-y-3 text-stone-100" data-id="element-511" data-mp="3k4p87buekdmn">
                  <p data-id="element-512" data-mp="366sc0gjv298i">• Months of wasted progress</p>
                  <p data-id="element-513" data-mp="3b64xawv3388z">• Thousands of dollars on bunk products</p>
                  <p data-id="element-514" data-mp="3b4k5vg42g5fc">• Potential damage to your organs</p>
                  <p data-id="element-515" data-mp="1urviougn8xvc">
                    • The irreplaceable resource of TIME as your biology
                    continues to decline
                  </p>
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-950/40 to-black border-2 border-purple-500/50 p-6 my-8 rounded-lg" data-id="element-516" data-mp="5b9hw4ym3t0v">
                <p className="text-xl font-bold text-purple-400 mb-4" data-id="element-517" data-mp="cgdr357tedd">
                  PSI PRO pays for itself if it saves you from a single misstep.
                </p>
                <div className="space-y-3 text-stone-100" data-id="element-518" data-mp="3nnxuyx5noutk">
                  <p data-id="element-519" data-mp="3ipwcadc56xim">
                    And you're getting ongoing guidance from the top minds in
                    the field, month after month.
                  </p>
                  <p data-id="element-520" data-mp="3dpi48vajjnkl">Plus Peptide Mastery 2.0 included with your membership.</p>
                  <p data-id="element-521" data-mp="38shy6kdoykth">Plus the Precision Cell™ methodology.</p>
                  <p data-id="element-522" data-mp="34qelxtj77c75">Plus the paradigm shift that changes everything.</p>
                </div>
              </div>

              <div className="text-center mt-8" data-id="element-523" data-mp="170mzr6gg4ct7">
                <p className="text-2xl font-bold text-blue-400" data-id="element-524" data-mp="16y95gztlb4mp">Do the math.</p>
              </div>
            </div>

            {/* CTA */}
            <a href="https://go.peptidescienceinstitute.com/pm-pro-monthly-fm" className="block w-full px-5 sm:px-8 py-5 sm:py-6 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white text-center font-bold text-base sm:text-xl rounded-lg transition-all duration-300 shadow-[0_0_24px_rgba(59,130,246,0.4)] sm:shadow-[0_0_40px_rgba(59,130,246,0.5)] sm:hover:shadow-[0_0_60px_rgba(59,130,246,0.7)] sm:hover:scale-105 min-h-[44px]" data-id="element-525" data-mp="2kwwqhb335fpw">
              <span className="hidden sm:inline" data-id="element-526" data-mp="21zvkbmxsp9kd">→ </span>START YOUR MEMBERSHIP<span className="hidden sm:inline" data-id="element-527" data-mp="26xezcp2bhqdd"> ←</span>
            </a>
          </div>
        </div>

        {/* FOOTER */}
        <div className="relative" data-id="element-528" data-mp="2b0bluquza6ml">
          <div className="text-center space-y-6 py-12 border-t border-purple-500/30" data-id="element-529" data-mp="2fxtjvm7gj1ft">
            <div data-id="element-530" data-mp="3mdme5ahgrvgd">
              <h4 className="text-3xl font-serif font-bold text-purple-400 mb-2" style={{
              fontFamily: 'Cinzel, serif',
              textShadow: '0 0 20px rgba(168, 85, 247, 0.4)'
            }} data-id="element-531" data-mp="3me5krv1nyxf3">
                The Peptide Science Institute
              </h4>
              <p className="text-lg text-stone-400 italic" data-id="element-532" data-mp="3kw6yzf2yabea">
                Rewriting Biology. Transcending Limitations.
              </p>
            </div>

            <div className="flex items-center justify-center gap-3 text-stone-300" data-id="element-533" data-mp="2s5aeecrre2z">
              <Mail className="w-5 h-5 text-purple-400" data-id="element-534" data-mp="3utld381eio3f" />
              <p data-id="element-535" data-mp="cors4u9stp0s">
                Questions? Email us:{' '}
                <a href="mailto:support@support.peptidescienceinstitute.com" className="text-purple-400 hover:text-purple-300 transition-colors underline" data-id="element-536" data-mp="cmx9jmm028zv">
                  support@support.peptidescienceinstitute.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}